import { useCognitoAuth } from "@/hooks/useCognitoAuth";
import { isInternalAdminEmail, canUseV2WorkspaceAdmin } from "@/lib/internalAccess";
import { emailFromIdToken } from "@/lib/cognito";

/**
 * Gate for the new-admin rollout.
 *
 * `isInternal` is the master "show the real new-admin content (and fetch its data)
 * vs. the classic-admin placeholder" switch used across every workspace page. It is
 * true when the signed-in user may use the v2 workspace admin — i.e. the internal
 * builder (INTERNAL_ADMIN_EMAILS) OR anyone allowed by the pilot rollout
 * (canUseV2WorkspaceAdmin: the pilot allow-list now, EVERYONE from the scheduled
 * cutover). This MUST track the same predicate the access gates use (RequireMfa +
 * WorkspaceLayout); otherwise a pilot/rolled-out user gets *into* the admin but every
 * page shows the "being built" placeholder with no content. Not a security boundary —
 * the backend still authorises every request by token + role.
 */
export const useInternalAdmin = () => {
  const { user, tokens, isLoading, isAuthenticated } = useCognitoAuth();

  // Prefer the ID-token `email` claim — it survives reloads and token-seeded
  // sessions, unlike `signInDetails.loginId` (and `username` may be the sub).
  const email =
    emailFromIdToken(tokens.idToken) || user?.signInDetails?.loginId || user?.username || null;
  const isInternal =
    isAuthenticated && (isInternalAdminEmail(email) || canUseV2WorkspaceAdmin(email));

  return { isInternal, isLoading, email };
};
